import React, { useEffect, useState, useCallback } from 'react';
import Layout from '../components/Layout';
import { getApiUsageAll, getApiUsageService } from '../utils/api';
import { BarChart3, Activity, Building2, Zap, ArrowLeft, Download, RefreshCw } from 'lucide-react';

const SERVICE_META = {
  google_maps:  { name: 'Google Maps / Places', abbr: 'GM', color: '#0d9488' },
  fax_plus:     { name: 'Fax.plus',             abbr: 'FX', color: '#8b5cf6' },
  ringcentral:  { name: 'RingCentral',          abbr: 'RC', color: '#0ea5e9' },
  resend:       { name: 'Resend (Email)',        abbr: 'EM', color: '#f97316' },
  ocr:          { name: 'OCR Processing',        abbr: 'OC', color: '#ec4899' },
  ai:           { name: 'AI / LLM',             abbr: 'AI', color: '#6366f1' },
  twilio:       { name: 'Twilio (SMS)',          abbr: 'TW', color: '#ef4444' },
  esign:        { name: 'E-Signature',           abbr: 'ES', color: '#22c55e' },
};

function getSvcMeta(key) {
  return SERVICE_META[key] || { name: key, abbr: key.slice(0, 2).toUpperCase(), color: '#64748b' };
}

function UsageBar({ value, max, color = '#0d9488' }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ flex: 1, height: 6, borderRadius: 3, background: 'var(--border)', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 3, transition: 'width 0.3s' }} />
      </div>
      <span style={{ fontSize: '0.78rem', fontFamily: 'var(--font-mono)', color: 'var(--text-muted)', minWidth: 42, textAlign: 'right' }}>
        {value.toLocaleString()}
      </span>
    </div>
  );
}

function KpiCard({ label, value, sub, icon: Icon, color }) {
  return (
    <div style={{ padding: '16px 18px', background: 'var(--bg-card)', borderRadius: 10, border: '1px solid var(--border)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <span style={{ fontSize: '0.68rem', color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{label}</span>
        {Icon && <Icon size={14} style={{ color: color || 'var(--text-muted)' }} />}
      </div>
      <div style={{ fontSize: '1.6rem', fontWeight: 800, color: color || 'var(--gold)', lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

export default function ApiUsagePage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [drillService, setDrillService] = useState(null);
  const [drillData, setDrillData] = useState(null);
  const [drillLoading, setDrillLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await getApiUsageAll({ days });
      setData(d);
    } catch (err) { console.error('Failed to load API usage:', err); }
    setLoading(false);
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const loadDrill = useCallback(async (service) => {
    setDrillLoading(true);
    try {
      const d = await getApiUsageService(service, { days });
      setDrillData(d);
    } catch { setDrillData(null); }
    setDrillLoading(false);
  }, [days]);

  const handleDrill = (service) => {
    setDrillService(service);
    loadDrill(service);
  };

  const handleBack = () => {
    setDrillService(null);
    setDrillData(null);
  };

  // Export CSV
  const handleExport = () => {
    if (!data?.orgs?.length) return;
    const services = data.services?.map(s => s.service) || [];
    const header = ['Organization', 'Total', ...services.map(s => getSvcMeta(s).name)].join(',');
    const rows = data.orgs.map(o => {
      const svcCols = services.map(s => o.byService?.[s] || 0);
      return ['"' + (o.name || o.org_id) + '"', o.total, ...svcCols].join(',');
    });
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `api-usage-${days}d-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Chart data for daily
  const dailyData = data?.daily || [];
  const maxDaily = Math.max(...dailyData.map(d => d.count), 1);
  const maxSvc = data?.services?.length ? Math.max(...data.services.map(s => s.total)) : 1;
  const maxOrg = data?.orgs?.length ? Math.max(...data.orgs.map(o => o.total)) : 1;

  // Drill view chart data
  const drillDaily = (() => {
    if (!drillData?.raw?.length) return [];
    const byDate = {};
    drillData.raw.forEach(r => { byDate[r.usage_date] = (byDate[r.usage_date] || 0) + r.call_count; });
    const out = [];
    for (let i = 13; i >= 0; i--) {
      const d = new Date(); d.setDate(d.getDate() - i);
      const key = d.toISOString().split('T')[0];
      out.push({ date: key, label: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), count: byDate[key] || 0 });
    }
    return out;
  })();
  const maxDrillDaily = Math.max(...drillDaily.map(d => d.count), 1);

  return (
    <Layout>
      <div className="page-header">
        <div>
          {drillService ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <button onClick={handleBack} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}>
                <ArrowLeft size={18} />
              </button>
              <div>
                <h1 className="page-title">{getSvcMeta(drillService).name} Usage</h1>
                <p className="page-subtitle">Detailed usage breakdown by organization</p>
              </div>
            </div>
          ) : (
            <>
              <h1 className="page-title">API Usage</h1>
              <p className="page-subtitle">Platform-wide API consumption across all services and organizations</p>
            </>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {!drillService && (
            <button className="btn btn-sm" onClick={handleExport} disabled={!data?.orgs?.length}
              style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.78rem' }}>
              <Download size={13} /> Export CSV
            </button>
          )}
          <button className="btn btn-sm" onClick={drillService ? () => loadDrill(drillService) : load}
            style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.78rem' }}>
            <RefreshCw size={13} /> Refresh
          </button>
        </div>
      </div>

      {/* Date range pills */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 20, flexWrap: 'wrap' }}>
        {[7, 14, 30, 90].map(d => (
          <button key={d} onClick={() => { setDays(d); if (drillService) loadDrill(drillService); }}
            style={{
              padding: '6px 14px', borderRadius: 20, cursor: 'pointer', fontWeight: days === d ? 700 : 400,
              fontSize: '0.82rem', fontFamily: 'inherit',
              background: days === d ? 'var(--gold)' : 'var(--bg-card)',
              color: days === d ? '#0c1628' : 'var(--text-muted)',
              border: `1px solid ${days === d ? 'var(--gold)' : 'var(--border)'}`,
            }}>{d === 7 ? '7 days' : d === 14 ? '14 days' : d === 30 ? '30 days' : '90 days'}</button>
        ))}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-muted)' }}>
          <div className="spinner" style={{ margin: '0 auto 12px' }} />
          Loading API usage data...
        </div>
      ) : drillService ? (
        /* ─── DRILL-DOWN VIEW ─── */
        drillLoading ? (
          <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-muted)' }}>Loading...</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* KPIs */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
              <KpiCard label="Total Calls" value={(drillData?.total || 0).toLocaleString()} icon={Zap} color={getSvcMeta(drillService).color} />
              <KpiCard label="Active Orgs" value={drillData?.orgs?.length || 0} icon={Building2} color="var(--blue)" />
              <KpiCard label="Daily Avg" value={drillData?.total ? Math.round(drillData.total / days) : 0} icon={Activity} color="var(--purple)" />
            </div>

            {/* Daily chart */}
            {drillDaily.length > 0 && (
              <div className="card" style={{ padding: '16px 18px' }}>
                <div style={{ fontWeight: 700, fontSize: '0.88rem', color: 'var(--text-primary)', marginBottom: 14 }}>Daily Calls (Last 14 Days)</div>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 70 }}>
                  {drillDaily.map(d => {
                    const h = maxDrillDaily > 0 ? Math.max(3, Math.round((d.count / maxDrillDaily) * 62)) : 3;
                    return (
                      <div key={d.date} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                        <div title={`${d.label}: ${d.count}`} style={{
                          width: '100%', height: h, borderRadius: '3px 3px 0 0',
                          background: d.count > 0 ? getSvcMeta(drillService).color : 'var(--border)',
                        }} />
                        <span style={{ fontSize: '0.48rem', color: 'var(--text-muted)', transform: 'rotate(-35deg)', transformOrigin: 'center', whiteSpace: 'nowrap' }}>{d.label}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Org table */}
            {drillData?.orgs?.length > 0 && (
              <div className="card" style={{ overflow: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: 'var(--bg-base)' }}>
                      <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)' }}>Organization</th>
                      <th style={{ padding: '10px 16px', textAlign: 'right', fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)', width: 80 }}>Calls</th>
                      <th style={{ padding: '10px 16px', fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)', width: 200 }}>Usage</th>
                    </tr>
                  </thead>
                  <tbody>{drillData.orgs.map((o, i) => (
                    <tr key={o.org_id} style={{ borderTop: '1px solid var(--border)', background: i % 2 === 0 ? 'transparent' : 'var(--bg-base)' }}>
                      <td style={{ padding: '10px 16px', fontSize: '0.85rem', fontWeight: 600 }}>{o.name || o.org_id}</td>
                      <td style={{ padding: '10px 16px', textAlign: 'right', fontFamily: 'var(--font-mono)', color: getSvcMeta(drillService).color, fontWeight: 700 }}>{o.total.toLocaleString()}</td>
                      <td style={{ padding: '10px 16px' }}><UsageBar value={o.total} max={Math.max(...drillData.orgs.map(x => x.total))} color={getSvcMeta(drillService).color} /></td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            )}
            {!drillData?.orgs?.length && <div style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.85rem', padding: 30 }}>No usage recorded for this service in the selected period.</div>}
          </div>
        )
      ) : (
        /* ─── OVERVIEW ─── */
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* KPI Row */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
            <KpiCard label="Total API Calls" value={(data?.total || 0).toLocaleString()} sub={`Last ${days} days`} icon={Zap} color="var(--gold)" />
            <KpiCard label="Active Orgs" value={data?.active_orgs || 0} icon={Building2} color="var(--blue)" />
            <KpiCard label="Services Used" value={data?.services?.length || 0} icon={Activity} color="var(--purple)" />
            <KpiCard label="Daily Average" value={data?.total ? Math.round(data.total / days) : 0} sub="calls/day" icon={BarChart3} color="#0d9488" />
          </div>

          {/* Daily Chart */}
          {dailyData.length > 0 && (
            <div className="card" style={{ padding: '18px 20px' }}>
              <div style={{ fontWeight: 700, fontSize: '0.92rem', color: 'var(--text-primary)', marginBottom: 14 }}>Daily API Calls (All Services)</div>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 80 }}>
                {dailyData.map(d => {
                  const h = maxDaily > 0 ? Math.max(3, Math.round((d.count / maxDaily) * 72)) : 3;
                  const dateObj = new Date(d.date + 'T12:00:00');
                  const label = dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                  return (
                    <div key={d.date} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                      <span style={{ fontSize: '0.55rem', fontFamily: 'var(--font-mono)', color: 'var(--text-muted)', fontWeight: 600 }}>
                        {d.count > 0 ? d.count : ''}
                      </span>
                      <div title={`${label}: ${d.count} calls`} style={{
                        width: '100%', height: h, borderRadius: '3px 3px 0 0',
                        background: d.count > 0 ? 'var(--gold)' : 'var(--border)',
                        transition: 'height 0.3s',
                      }} />
                      {dailyData.length <= 14 && (
                        <span style={{ fontSize: '0.48rem', color: 'var(--text-muted)', transform: 'rotate(-35deg)', transformOrigin: 'center', whiteSpace: 'nowrap' }}>{label}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Service Breakdown */}
          <div className="card" style={{ overflow: 'auto' }}>
            <div className="card-header">
              <h3 className="card-title">Service Breakdown</h3>
            </div>
            {data?.services?.length > 0 ? (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: 'var(--bg-base)' }}>
                    <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)' }}>Service</th>
                    <th style={{ padding: '10px 16px', textAlign: 'right', fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)', width: 90 }}>Total Calls</th>
                    <th style={{ padding: '10px 16px', textAlign: 'right', fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)', width: 80 }}>Orgs</th>
                    <th style={{ padding: '10px 16px', fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)', width: 200 }}>Volume</th>
                    <th style={{ padding: '10px 16px', width: 80 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {data.services.map((s, i) => {
                    const meta = getSvcMeta(s.service);
                    return (
                      <tr key={s.service} style={{ borderTop: '1px solid var(--border)', background: i % 2 === 0 ? 'transparent' : 'var(--bg-base)' }}>
                        <td style={{ padding: '10px 16px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div style={{
                              width: 30, height: 30, borderRadius: 7,
                              background: `${meta.color}18`, border: `1px solid ${meta.color}40`,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontFamily: 'var(--font-mono)', fontWeight: 800, fontSize: '0.6rem', color: meta.color,
                            }}>{meta.abbr}</div>
                            <span style={{ fontWeight: 600, fontSize: '0.88rem' }}>{meta.name}</span>
                          </div>
                        </td>
                        <td style={{ padding: '10px 16px', textAlign: 'right', fontFamily: 'var(--font-mono)', fontWeight: 700, color: meta.color }}>{s.total.toLocaleString()}</td>
                        <td style={{ padding: '10px 16px', textAlign: 'right', fontSize: '0.85rem', color: 'var(--text-muted)' }}>{s.orgs}</td>
                        <td style={{ padding: '10px 16px' }}><UsageBar value={s.total} max={maxSvc} color={meta.color} /></td>
                        <td style={{ padding: '10px 16px' }}>
                          <button onClick={() => handleDrill(s.service)}
                            style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: '0.72rem', fontWeight: 600, color: 'var(--gold)', fontFamily: 'inherit' }}>
                            Details
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <div style={{ textAlign: 'center', padding: 30, color: 'var(--text-muted)', fontSize: '0.85rem' }}>No API usage recorded in the selected period.</div>
            )}
          </div>

          {/* Organization Breakdown */}
          <div className="card" style={{ overflow: 'auto' }}>
            <div className="card-header">
              <h3 className="card-title">Usage by Organization</h3>
            </div>
            {data?.orgs?.length > 0 ? (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: 'var(--bg-base)' }}>
                    <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)' }}>Organization</th>
                    <th style={{ padding: '10px 16px', textAlign: 'right', fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)', width: 90 }}>Total</th>
                    {data.services?.map(s => (
                      <th key={s.service} style={{ padding: '10px 16px', textAlign: 'right', fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', color: getSvcMeta(s.service).color, width: 70 }}>
                        {getSvcMeta(s.service).abbr}
                      </th>
                    ))}
                    <th style={{ padding: '10px 16px', fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)', width: 180 }}>Volume</th>
                  </tr>
                </thead>
                <tbody>
                  {data.orgs.slice(0, 25).map((o, i) => (
                    <tr key={o.org_id} style={{ borderTop: '1px solid var(--border)', background: i % 2 === 0 ? 'transparent' : 'var(--bg-base)' }}>
                      <td style={{ padding: '10px 16px', fontSize: '0.85rem', fontWeight: 600 }}>{o.name || o.org_id}</td>
                      <td style={{ padding: '10px 16px', textAlign: 'right', fontFamily: 'var(--font-mono)', fontWeight: 700, color: 'var(--gold)' }}>{o.total.toLocaleString()}</td>
                      {data.services?.map(s => (
                        <td key={s.service} style={{ padding: '10px 16px', textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: '0.82rem', color: (o.byService?.[s.service] || 0) > 0 ? 'var(--text-primary)' : 'var(--text-muted)' }}>
                          {(o.byService?.[s.service] || 0).toLocaleString()}
                        </td>
                      ))}
                      <td style={{ padding: '10px 16px' }}><UsageBar value={o.total} max={maxOrg} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div style={{ textAlign: 'center', padding: 30, color: 'var(--text-muted)', fontSize: '0.85rem' }}>No organization usage recorded.</div>
            )}
            {data?.orgs?.length > 25 && (
              <div style={{ padding: '10px 16px', fontSize: '0.78rem', color: 'var(--text-muted)', borderTop: '1px solid var(--border)' }}>
                Showing top 25 of {data.orgs.length} organizations. Export CSV for the full list.
              </div>
            )}
          </div>
        </div>
      )}
    </Layout>
  );
}
